import React, { memo, useState, useCallback, useEffect } from 'react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { 
  Bot, Brain, Sparkles, FileText, FileSpreadsheet, Presentation, 
  Download, Eye, RefreshCw, Clock, CheckCircle, AlertCircle,
  ArrowRight, Users, Zap, ChevronDown, ChevronRight, ExternalLink,
  FileIcon, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { aiWorkforceAPI } from '../../api';

// Brain Analysis Display Component
const BrainAnalysisCard = memo(({ title, icon: Icon, color, assessment, isLoading }) => (
  <Card className={`border-l-4 ${color}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
        {assessment?.confidence_score && (
          <Badge variant="outline" className="ml-auto text-xs">
            {assessment.confidence_score.toFixed(0)}% confidence
          </Badge>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Processing...</span>
        </div>
      ) : assessment ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground line-clamp-3">
            {assessment.assessment?.substring(0, 300)}...
          </p>
          {assessment.key_points?.length > 0 && (
            <div className="space-y-1">
              {assessment.key_points.slice(0, 3).map((point, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          )}
          {assessment.processing_time_ms && (
            <p className="text-xs text-muted-foreground">
              Processed in {(assessment.processing_time_ms / 1000).toFixed(2)}s
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Not yet processed</p>
      )}
    </CardContent>
  </Card>
));

BrainAnalysisCard.displayName = 'BrainAnalysisCard';

// Deliverable Item Component
const DeliverableItem = memo(({ deliverable, onView, onDownload }) => {
  const iconMap = {
    word: FileText,
    excel: FileSpreadsheet,
    powerpoint: Presentation,
    pdf: FileIcon,
  };
  const DeliverableIcon = iconMap[deliverable.type] || FileText;
  
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          deliverable.type === 'word' ? 'bg-blue-100 text-blue-600' :
          deliverable.type === 'excel' ? 'bg-emerald-100 text-emerald-600' :
          deliverable.type === 'powerpoint' ? 'bg-orange-100 text-orange-600' :
          'bg-slate-100 text-slate-600'
        }`}>
          <DeliverableIcon className="h-5 w-5" />
        </div>
        <div>
          <p className="font-medium text-sm">{deliverable.title || deliverable.name}</p>
          <p className="text-xs text-muted-foreground">
            {deliverable.type?.toUpperCase()} • {format(new Date(deliverable.created_at), 'MMM d, yyyy')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onView(deliverable)} data-testid={`view-deliverable-${deliverable.id}`}>
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDownload(deliverable)} data-testid={`download-deliverable-${deliverable.id}`}>
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

DeliverableItem.displayName = 'DeliverableItem';

// AI Task Card with expandable details
const AITaskCard = memo(({ task, onViewDetails, onViewDeliverables }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'in_progress': case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending_review': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'failed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <Card className="overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Bot className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{task.title}</p>
                {task.has_brain_analysis && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                    <Brain className="h-3 w-3 mr-1" /> 3-Brain
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground">
                  {task.assigned_agent_name || task.agent_type}
                </span>
                {task.collaborators?.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    +{task.collaborators.length} agents
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(task.status)}>
              {task.status?.replace('_', ' ')}
            </Badge>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        {(task.status === 'in_progress' || task.status === 'processing') && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Processing...</span>
              <span>{task.progress || 0}%</span>
            </div>
            <Progress value={task.progress || 0} className="h-1" />
          </div>
        )}
      </div>
      
      {expanded && (
        <div className="border-t px-4 py-3 bg-slate-50/50 space-y-3">
          {/* Task Description */}
          {task.description && (
            <p className="text-sm text-muted-foreground">{task.description}</p>
          )}
          
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-purple-600">{task.deliverables_count || 0}</p>
              <p className="text-xs text-muted-foreground">Deliverables</p>
            </div>
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-blue-600">{task.collaborators?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Collaborators</p>
            </div>
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-emerald-600">
                {task.total_processing_time_ms ? `${(task.total_processing_time_ms / 1000).toFixed(1)}s` : '-'}
              </p>
              <p className="text-xs text-muted-foreground">Process Time</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={(e) => { e.stopPropagation(); onViewDetails(task); }}
              data-testid={`view-task-details-${task.id}`}
            >
              <Brain className="h-4 w-4 mr-2" />
              View Analysis
            </Button>
            {(task.deliverables_count > 0 || task.deliverable_ids?.length > 0) && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={(e) => { e.stopPropagation(); onViewDeliverables(task); }}
                data-testid={`view-deliverables-${task.id}`}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Deliverables
              </Button>
            )}
          </div>
          
          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <span>Created: {format(new Date(task.created_at), 'MMM d, yyyy h:mm a')}</span>
            {task.completed_at && (
              <span>Completed: {format(new Date(task.completed_at), 'MMM d, yyyy h:mm a')}</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
});

AITaskCard.displayName = 'AITaskCard';

// Task Details Dialog with Brain Analysis
const TaskDetailsDialog = memo(({ open, onOpenChange, task }) => {
  if (!task) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            AI Task Analysis: {task.title}
          </DialogTitle>
          <DialogDescription>
            Multi-brain analysis by {task.assigned_agent_name || task.agent_type}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* 3-Brain Analysis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BrainAnalysisCard
              title="Left Brain (GPT-4o)"
              icon={Zap}
              color="border-l-blue-500"
              assessment={task.reasoning?.left_brain}
              isLoading={task.status === 'processing' && !task.reasoning?.left_brain}
            />
            <BrainAnalysisCard
              title="Right Brain (Claude)"
              icon={Sparkles}
              color="border-l-purple-500"
              assessment={task.reasoning?.right_brain}
              isLoading={task.status === 'processing' && !task.reasoning?.right_brain}
            />
            <BrainAnalysisCard
              title="Cortex (Gemini)"
              icon={Brain}
              color="border-l-emerald-500"
              assessment={task.reasoning?.cortex}
              isLoading={task.status === 'processing' && !task.reasoning?.cortex}
            />
          </div>
          
          {/* Final Output */}
          {task.reasoning?.final_output && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Synthesized Output
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{task.reasoning.final_output}</p>
                
                {task.reasoning.final_recommendations?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Recommendations:</p>
                    <ul className="space-y-1">
                      {task.reasoning.final_recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <ArrowRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Collaborators */}
          {task.collaborators?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  AI Collaborators ({task.collaborators.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {task.collaborators.map((collab, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium">{collab.agent_name || collab.agent_type}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">{collab.purpose || 'Consultation'}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

TaskDetailsDialog.displayName = 'TaskDetailsDialog';

// Deliverables Dialog
const DeliverablesDialog = memo(({ open, onOpenChange, task, deliverables, onDownload }) => {
  if (!task) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Deliverables: {task.title}
          </DialogTitle>
          <DialogDescription>
            Generated documents and outputs from this AI task
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {deliverables?.length > 0 ? (
            deliverables.map(deliverable => (
              <DeliverableItem
                key={deliverable.id}
                deliverable={deliverable}
                onView={() => onDownload(deliverable)} // Use download instead of view
                onDownload={() => onDownload(deliverable)}
              />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No deliverables generated yet</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

DeliverablesDialog.displayName = 'DeliverablesDialog';

// Main AITasksTab Component
const AITasksTab = memo(({ contactId, tasks = [] }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeliverablesDialog, setShowDeliverablesDialog] = useState(false);
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  // Filter AI-assigned tasks
  const aiTasks = React.useMemo(() => 
    tasks.filter(t => t.assigned_to_type === 'ai_agent'),
    [tasks]
  );

  const filteredTasks = React.useMemo(() => {
    if (filter === 'all') return aiTasks;
    return aiTasks.filter(t => t.status === filter);
  }, [aiTasks, filter]);

  const handleViewDetails = useCallback(async (task) => {
    setSelectedTask(task);
    setShowDetailsDialog(true);
    
    // Fetch full task details with reasoning if not already loaded
    if (!task.reasoning && task.reasoning_id) {
      try {
        const res = await aiWorkforceAPI.getTaskReasoning(task.id);
        setSelectedTask(prev => ({ ...prev, reasoning: res.data }));
      } catch (error) {
        console.error('Failed to fetch task reasoning:', error);
      }
    }
  }, []);

  const handleViewDeliverables = useCallback(async (task) => {
    setSelectedTask(task);
    setLoading(true);
    
    try {
      const res = await aiWorkforceAPI.getTaskDeliverables(task.id);
      setDeliverables(res.data.deliverables || res.data || []);
      setShowDeliverablesDialog(true);
    } catch (error) {
      toast.error('Failed to load deliverables');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownloadDeliverable = useCallback(async (deliverable) => {
    try {
      let response;
      switch (deliverable.type) {
        case 'excel':
          response = await aiWorkforceAPI.exportToExcel(deliverable.id);
          break;
        case 'word':
          response = await aiWorkforceAPI.exportToWord(deliverable.id);
          break;
        default:
          if (deliverable.url || deliverable.file_url) {
            // Use authenticated download
            try {
              const downloadUrl = deliverable.url || deliverable.file_url;
              const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`;
              const token = localStorage.getItem('token');
              const fetchResponse = await fetch(fullUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (!fetchResponse.ok) throw new Error('Download failed');
              const contentType = fetchResponse.headers.get('content-type') || 'application/octet-stream';
              const blob = await fetchResponse.blob();
              const blobUrl = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = blobUrl;
              // Ensure proper file extension
              let filename = deliverable.filename || deliverable.title || 'document';
              if (!filename.includes('.')) {
                if (contentType.includes('pdf')) filename += '.pdf';
                else if (contentType.includes('word') || contentType.includes('docx')) filename += '.docx';
                else if (contentType.includes('text')) filename += '.txt';
              }
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(blobUrl);
              document.body.removeChild(a);
              toast.success('Download complete!');
            } catch (error) {
              console.error('Download error:', error);
              toast.error('Failed to download file');
            }
            return;
          }
          toast.error('Download not available for this type');
          return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${deliverable.title || 'deliverable'}.${deliverable.type === 'excel' ? 'xlsx' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download deliverable');
    }
  }, []);

  // Stats
  const stats = React.useMemo(() => ({
    total: aiTasks.length,
    completed: aiTasks.filter(t => t.status === 'completed').length,
    inProgress: aiTasks.filter(t => ['in_progress', 'processing'].includes(t.status)).length,
    pending: aiTasks.filter(t => ['pending', 'pending_review'].includes(t.status)).length
  }), [aiTasks]);

  if (aiTasks.length === 0) {
    return (
      <div className="text-center py-12">
        <Bot className="h-16 w-16 text-purple-200 mx-auto mb-4" />
        <h3 className="font-medium text-lg mb-2">No AI Tasks</h3>
        <p className="text-muted-foreground text-sm mb-4">
          Tasks assigned to AI agents will appear here with their analysis and deliverables
        </p>
        <Badge variant="outline" className="bg-purple-50 text-purple-700">
          <Sparkles className="h-3 w-3 mr-1" />
          Assign tasks to AI agents to see the 3-Brain analysis
        </Badge>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[600px]">
      <div className="p-4 space-y-4">
        {/* Header with Stats */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              AI Tasks
            </h3>
            <p className="text-sm text-muted-foreground">
              Tasks processed by AI agents with 3-Brain analysis
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('completed')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('in_progress')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('pending')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Badge */}
        {filter !== 'all' && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">
              Showing: {filter.replace('_', ' ')}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
              Clear Filter
            </Button>
          </div>
        )}

        {/* Task List */}
        <div className="space-y-3">
          {filteredTasks.map(task => (
            <AITaskCard
              key={task.id}
              task={task}
              onViewDetails={handleViewDetails}
              onViewDeliverables={handleViewDeliverables}
            />
          ))}
        </div>

        {filteredTasks.length === 0 && filter !== 'all' && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No tasks with status &quot;{filter.replace('_', ' ')}&quot;</p>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <TaskDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        task={selectedTask}
      />
      
      <DeliverablesDialog
        open={showDeliverablesDialog}
        onOpenChange={setShowDeliverablesDialog}
        task={selectedTask}
        deliverables={deliverables}
        onDownload={handleDownloadDeliverable}
      />
    </ScrollArea>
  );
});

AITasksTab.displayName = 'AITasksTab';

export default AITasksTab;
